import React, { useState, useEffect, useRef } from 'react';
import ChatWindow from '../components/ChatWindow';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { trackAskQuestion } from '../services/analytics';

export default function AskNitin() {
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState('');
  const [tokenBalance, setTokenBalance] = useState(user?.tokenBalance ?? 0);
  const [toast, setToast] = useState('');
  const textareaRef = useRef(null);
  const WORD_LIMIT = 40;

  useEffect(() => { loadHistory(); refreshUser(); }, []);

  // Keep local balance in sync with auth context
  useEffect(() => {
    if (user?.tokenBalance !== undefined) setTokenBalance(user.tokenBalance);
  }, [user?.tokenBalance]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/ask/history?limit=20');
      setMessages([...data.chats].reverse());
    } catch {}
    finally { setHistoryLoading(false); }
  };

  const isExhausted = tokenBalance <= 0;

  const askQuestion = async (question) => {
    if (!question || loading || isExhausted) return;
    setInput('');
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/ask', { question });
      setMessages((prev) => [...prev, data]);
      trackAskQuestion();
      if (data.tokenBalance !== undefined) {
        setTokenBalance(data.tokenBalance);
        refreshUser();
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'TOKEN_EXHAUSTED') {
        setTokenBalance(0);
        setError('You have run out of tokens. Contact an admin to get more.');
      } else {
        setError(err.response?.data?.error || 'Failed to get an answer. Please try again.');
      }
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const wordCount = input.trim() ? input.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > WORD_LIMIT;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (overLimit) {
      showToast(`Please keep your question under ${WORD_LIMIT} words`);
      return;
    }
    askQuestion(input.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
  };

  const balanceColor = tokenBalance > 10000 ? '#4ade80' : tokenBalance > 2000 ? '#fbbf24' : '#f87171';

  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            <div style={s.headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <div>
              <h1 style={s.title}>Ask Nitin</h1>
              <p style={s.subtitle}>Ask anything about your career - internships, practice area, your first job - and get straight, experience-backed guidance.</p>
            </div>
          </div>
          {/* Token balance pill */}
          <div style={{ ...s.balancePill, borderColor: `${balanceColor}40` }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={balanceColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ ...s.balanceNum, color: balanceColor }}>
              {tokenBalance.toLocaleString()}
            </span>
            <span style={s.balanceLabel}>tokens</span>
          </div>
        </div>

        {/* Token exhausted banner */}
        {isExhausted && (
          <div style={s.exhaustedBanner}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>
              <strong style={{ display: 'block', marginBottom: '2px' }}>Token balance exhausted</strong>
              <span style={{ fontSize: '12px', opacity: 0.85 }}>Contact an admin or purchase tokens to continue chatting.</span>
            </div>
          </div>
        )}

        {/* Chat area */}
        <div style={s.chatArea}>
          {historyLoading ? (
            <div style={s.center}>
              <div style={s.spinner} />
              <p style={s.loadingText}>Loading conversation...</p>
            </div>
          ) : (
            <ChatWindow
            messages={messages}
            loading={loading}
            onSuggest={(q) => askQuestion(q)}
            userName={user?.name ? user.name.split(' ')[0] : ''}
          />
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={s.errorBar}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} style={s.inputArea}>
          <p style={{ ...s.wordCount, color: overLimit ? 'var(--danger, #dc2626)' : 'var(--text-3)' }}>
            {wordCount}/{WORD_LIMIT} words
          </p>
          <div style={s.inputWrap}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isExhausted ? 'Token balance exhausted. Contact an admin to continue.' : 'Ask a career question...'}
              style={{ ...s.textarea, opacity: isExhausted ? 0.5 : 1 }}
              className="nice-input"
              rows={2}
              disabled={loading || isExhausted}
            />
            <button
              type="submit"
              disabled={loading || !input.trim() || isExhausted || overLimit}
              style={{
                ...s.sendBtn,
                background: input.trim() && !loading && !isExhausted && !overLimit ? 'var(--navy)' : 'var(--border)',
                cursor: input.trim() && !loading && !isExhausted && !overLimit ? 'pointer' : 'not-allowed',
              }}
              className="primary-btn"
            >
              {loading
                ? <span style={s.spinner2} />
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                  </svg>
              }
            </button>
          </div>
          <p style={s.inputHint}>Enter to send · Shift+Enter for new line</p>
        </form>

        {/* Toast */}
        {toast && (
          <div style={s.toast}>{toast}</div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    height: 'calc(100dvh - 60px)', display: 'flex',
    flexDirection: 'column', padding: '16px',
    maxWidth: '880px', margin: '0 auto', width: '100%', boxSizing: 'border-box',
  },
  container: {
    flex: 1, display: 'flex', flexDirection: 'column', position: 'relative',
    background: '#fff', border: '1px solid var(--border)',
    borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow)',
  },
  header: {
    padding: '14px 18px',
    background: 'var(--navy)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerIcon: {
    width: '38px', height: '38px', borderRadius: '10px',
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-mid)',
  },
  title: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '16px', fontWeight: '700', color: '#fff',
  },
  subtitle: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '2px', lineHeight: '1.5' },
  balancePill: {
    display: 'flex', alignItems: 'center', gap: '5px',
    background: 'rgba(255,255,255,0.07)', padding: '5px 12px',
    borderRadius: '99px', border: '1px solid rgba(255,255,255,0.1)',
  },
  balanceNum: { fontSize: '13px', fontWeight: '700' },
  balanceLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.5)' },
  exhaustedBanner: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    background: 'var(--danger-bg)', color: 'var(--danger)',
    borderBottom: '1px solid rgba(185,28,28,0.15)',
    padding: '12px 18px', fontSize: '13px',
  },
  chatArea: {
    flex: 1, overflowY: 'auto', display: 'flex',
    flexDirection: 'column', background: 'var(--bg)',
  },
  center: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: '10px',
  },
  spinner: {
    width: '24px', height: '24px', borderRadius: '50%',
    border: '2px solid var(--border)', borderTop: '2px solid var(--navy)',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: { fontSize: '13px', color: 'var(--text-3)' },
  errorBar: {
    margin: '0 14px 10px', background: 'var(--danger-bg)',
    border: '1px solid rgba(185,28,28,0.2)', color: 'var(--danger)',
    padding: '10px 14px', borderRadius: '10px',
    fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  inputArea: {
    padding: '12px 14px 14px', borderTop: '1px solid var(--border-2)',
    background: '#fff', display: 'flex', flexDirection: 'column', gap: '6px',
  },
  inputWrap: { display: 'flex', gap: '10px', alignItems: 'flex-end' },
  textarea: {
    flex: 1, padding: '11px 14px',
    border: '1px solid var(--border)', borderRadius: '10px',
    fontSize: '16px', resize: 'none', lineHeight: '1.5',
    background: 'var(--bg)', color: 'var(--text)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  sendBtn: {
    width: '42px', height: '42px', color: '#fff', border: 'none',
    borderRadius: '10px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s',
  },
  spinner2: {
    width: '14px', height: '14px', borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff',
    animation: 'spin 0.7s linear infinite',
  },
  inputHint: { fontSize: '10px', color: 'var(--text-3)', paddingLeft: '2px' },
  wordCount: { fontSize: '10px', fontWeight: '600', paddingLeft: '2px', margin: '0 0 4px 0' },
  toast: {
    position: 'absolute', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
    background: 'var(--navy, #0c2340)', color: '#fff',
    padding: '10px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '500',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)', zIndex: 100,
    animation: 'fadeIn 0.2s ease-out',
  },
};
